import { useMemo } from 'react';
import { useFormState, useWatch } from 'react-hook-form';

import {
  CreateMessageCategoryArgsInput,
  UpdateMessageCategoryArgsInput,
} from 'modules/dragAndDropEditor/hooks/useManageMessageCategories';
import { ManageCustomCategoriesFormValues } from '../ManageCustomCategoriesFormProvider';
import { MANAGE_CUSTOM_CATEGORIES_FORM_FIELD_NAMES } from '../ManageCustomCategoriesModal.constants';
import { CategoriesFieldName } from '../ManageCustomCategoriesModal.types';

export type UseDirtyCategoriesReturnValue = {
  hasUnsavedChanges: boolean;
  update: UpdateMessageCategoryArgsInput;
  create: CreateMessageCategoryArgsInput;
};

/**
 * There are some longstanding issues with `useFieldArray` which make the isDirty field not reliable.
 * For example, when deleting the first element in a "Field Array", the rest of the elements are shifted down and marked as "dirty".
 * We also have a similar situation for `dirtyFields`. I created this hook as a workaround.
 * @see https://github.com/react-hook-form/react-hook-form/issues/2395
 */
export const useDirtyCategories = (): UseDirtyCategoriesReturnValue => {
  const { defaultValues } = useFormState<ManageCustomCategoriesFormValues>({
    name: MANAGE_CUSTOM_CATEGORIES_FORM_FIELD_NAMES.CATEGORIES,
  });
  const fields = useWatch<ManageCustomCategoriesFormValues, CategoriesFieldName>({
    name: MANAGE_CUSTOM_CATEGORIES_FORM_FIELD_NAMES.CATEGORIES,
  });

  const messageCategoriesMapper = useMemo(() => {
    return (
      defaultValues?.categories?.reduce(
        (acc, category) => {
          const categoryId = category?.categoryId as number;
          acc[categoryId] =
            category as ManageCustomCategoriesFormValues[CategoriesFieldName][number];
          return acc;
        },
        {} as Record<number, ManageCustomCategoriesFormValues[CategoriesFieldName][number]>,
      ) ?? {}
    );
  }, [defaultValues?.categories]);

  const values = useMemo(
    () =>
      fields.reduce(
        (acc, { name, categoryId }) => {
          const existingCategory = messageCategoriesMapper[categoryId as number];
          const { create, update } = acc;

          if (existingCategory) {
            if (existingCategory.name !== name) {
              update.push({ id: existingCategory.categoryId as number, name });
            }
          } else {
            create.push({ name });
          }

          return {
            create,
            update,
          };
        },
        {
          update: [] as UpdateMessageCategoryArgsInput,
          create: [] as CreateMessageCategoryArgsInput,
        },
      ),
    [fields, messageCategoriesMapper],
  );

  const hasUnsavedChanges = !!(values.create.length || values.update.length);

  return { ...values, hasUnsavedChanges };
};
